import { redirectToLogin } from '../loginActions'
import type { AttensiFetchError } from './AttensiFetchError'
import { createAttensiFetchError } from './createAttensiFetchError'
import { isAttensiFetchError } from './isAttensiFetchError'

type Headers = Record<string, string | undefined>

interface AttensiFetchConfig<Context> {
  getHeaders?: (context: Context | undefined) => Headers | Promise<Headers>
  onError?: (error: AttensiFetchError, context: Context | undefined) => void
}

type AttensiFetchInit<Context> = RequestInit & { context?: Context }

export function createAttensiFetch<Context = any>(
  config: AttensiFetchConfig<Context> = {}
) {
  return (url: RequestInfo | URL, init: AttensiFetchInit<Context> = {}) => {
    if (typeof url !== 'string') {
      throw Error('Input must be a string')
    }

    return attensiFetch(url, init, config)
  }
}

async function attensiFetch<Context>(
  url: string,
  init: AttensiFetchInit<Context>,
  config: AttensiFetchConfig<Context>
): Promise<Response> {
  try {
    const res = await fetch(url, await getParams(init, config))

    if (res.ok) return res

    if (res.status === 401) {
      redirectToLogin()
    }

    throw createAttensiFetchError({
      message: 'HTTP status code ' + res.status,
      status: res.status,
      text: await res.text(),
      method: init.method,
      url,
    })
  } catch (e: any) {
    if (isAttensiFetchError(e)) {
      config.onError?.(e, init.context)
      throw e
    }

    const error = createAttensiFetchError({
      message: e.message,
      cause: e,
      method: init.method,
      url,
    })
    config.onError?.(error, init.context)
    throw error
  }
}

async function getParams<Context>(
  init: AttensiFetchInit<Context>,
  config: AttensiFetchConfig<Context>
) {
  const customHeaders = await Promise.resolve(config.getHeaders?.(init.context))
  const headers = { ...customHeaders, ...init?.headers }
  return { ...init, headers }
}
